import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import AdminSidebar from "./AdminSidebar";
import AdminHeader from "./AdminHeader";
import AdminBreadcrumb from "./AdminBreadcrumb";
import AdminLoading from "./AdminLoading";
import { cn } from "../../utils/cn";
import { Suspense } from "react";

// Menu config shared between sidebar and header (for page title)
export const ADMIN_MENU_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: "LayoutDashboard", path: "/admin/dashboard" },
  { id: "product-types", label: "Product Types", icon: "FolderTree", path: "/admin/product-types" },
  { id: "products", label: "Products", icon: "Package", path: "/admin/products" },
  { id: "settings", label: "Settings", icon: "Settings", path: "/admin/settings" },
  { id: "roles", label: "Roles", icon: "Shield", path: "/admin/settings/roles" },
  { id: "permissions", label: "Permissions", icon: "Key", path: "/admin/settings/permissions" },
];

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" && window.innerWidth >= 1024);
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      if (desktop) setMobileMenuOpen(false);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className={cn("admin-layout min-h-screen flex overflow-visible bg-[var(--color-muted)]")}>
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden
        />
      )}

      <AdminSidebar
        sidebarOpen={sidebarOpen}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        menuItems={ADMIN_MENU_ITEMS}
      />

      {/* Main content: reserve space for fixed sidebar on desktop; full width on mobile */}
      <div
        className="flex-1 flex flex-col min-w-0 transition-[margin] duration-300 ease-in-out"
        style={{
          marginLeft: isDesktop ? (sidebarOpen ? 280 : 80) : 0,
        }}
      >
        <AdminHeader
          onMenuClick={() => setMobileMenuOpen(true)}
          onToggleSidebar={() => setSidebarOpen((o) => !o)}
          sidebarOpen={sidebarOpen}
          menuItems={ADMIN_MENU_ITEMS}
        />
        <main className="flex-1 overflow-x-auto p-4 sm:p-6">
          <AdminBreadcrumb />
          <Suspense fallback={<AdminLoading />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
